import React from 'react';
import { XIcon, PlusIcon } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { stickerTextStyle } from './StickerText';

interface AccountSwitcherModalProps {
    isOpen: boolean;
    onClose: () => void;
    loggedInAccounts: string[];
    currentUserId: string | null;
    usersData: any;
    onSwitchAccount: (username: string) => void;
    onAddAccount: () => void;
}

export const AccountSwitcherModal = ({
    isOpen,
    onClose,
    loggedInAccounts,
    currentUserId,
    usersData = {},
    onSwitchAccount,
    onAddAccount
}: AccountSwitcherModalProps) => {
    const { currentUser, usersData: authUsersData } = useAuth();
    if (!isOpen) return null;

    // Check if any of the logged-in accounts has admin privileges
    const hasAdminPrivileges = loggedInAccounts.some(username => {
        const user = usersData[username];
        return user?.role === 'admin' || username.toLowerCase() === 'agk';
    });

    const activeUserId = currentUserId || currentUser;
    const activeUsersData = { ...usersData, ...authUsersData };
    const currentUserData = activeUserId ? activeUsersData[activeUserId] : null;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const getUserPfp = (username: string) => {
        if (username === 'goodlyfe') return '/assets/goodieboy.png';
        const user = usersData[username];
        return user?.pfp || `https://api.dicebear.com/7.x/pixel-art/svg?seed=${username}`;
    };

    const getUserDisplayName = (username: string) => {
        if (username === 'goodlyfe') return 'GoodieBoy';
        const user = usersData[username];
        return user?.displayName || username;
    };

    // Prepare options list
    const options = [...loggedInAccounts];
    if (hasAdminPrivileges && !options.includes('goodlyfe')) {
        options.push('goodlyfe');
    }

    return (
        <div
            onClick={onClose}
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 animate-fade-in"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{ backgroundColor: modalBg, color: userTextColor }}
                className="relative rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col border border-white/10 shadow-2xl animate-scale-up"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/5">
                    <div>
                        <h2
                            className="text-xl font-black text-white tracking-tight"
                            style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}
                        >
                            Switch Account
                        </h2>
                        <p
                            className="text-xs text-gray-400 font-medium font-sans"
                            style={stickerTextStyle(0.18)}
                        >
                            Switch between your active profiles
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors"
                    >
                        <XIcon className="w-6 h-6 text-white/70" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                    <div className="grid grid-cols-1 gap-4">
                        {options.map(username => {
                            const isCurrent = currentUserId === username;
                            const isImpersonatedSystem = username === 'goodlyfe';
                            return (
                                <button
                                    key={username}
                                    onClick={() => {
                                        onSwitchAccount(username);
                                        onClose();
                                    }}
                                    style={{
                                        backgroundColor: isCurrent ? `${userThemeColor}1a` : 'rgba(0, 0, 0, 0.2)',
                                        borderColor: isCurrent ? `${userThemeColor}4d` : 'rgba(255, 255, 255, 0.1)'
                                    }}
                                    className={`group flex items-center gap-4 p-4 rounded-3xl border transition-all active:scale-[0.98] ${!isCurrent && 'hover:bg-white/5'
                                        }`}
                                >
                                    <div className="relative w-12 h-12 rounded-full overflow-hidden shadow-lg group-hover:scale-105 transition-transform bg-zinc-800">
                                        <img
                                            src={getUserPfp(username)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/pixel-art/svg?seed=${username}`;
                                            }}
                                        />
                                    </div>
                                    <div className="flex-1 text-left">
                                        <div className="flex items-center gap-1.5">
                                            <h3
                                                className="font-bold text-white text-sm"
                                                style={stickerTextStyle(0.18)}
                                            >
                                                {getUserDisplayName(username)}
                                            </h3>
                                            {isImpersonatedSystem && (
                                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-extrabold uppercase border border-purple-500/30">
                                                    Impersonate
                                                </span>
                                            )}
                                        </div>
                                        <p
                                            className="text-[10px] text-gray-400 font-sans"
                                            style={stickerTextStyle(0.18)}
                                        >
                                            @{username}
                                        </p>
                                    </div>
                                    {isCurrent ? (
                                        <div
                                            className="w-8 h-8 rounded-full flex items-center justify-center border"
                                            style={{
                                                backgroundColor: `${userThemeColor}33`,
                                                borderColor: `${userThemeColor}4d`
                                            }}
                                        >
                                            <div
                                                className="w-2.5 h-2.5 rounded-full animate-pulse"
                                                style={{ backgroundColor: userThemeColor }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="w-2 h-2 rounded-full bg-white/40" />
                                        </div>
                                    )}
                                </button>
                            );
                        })}

                        <button
                            onClick={() => {
                                onAddAccount();
                                onClose();
                            }}
                            style={{
                                borderColor: `${userThemeColor}33`,
                                color: userThemeColor,
                                backgroundColor: 'rgba(0, 0, 0, 0.1)'
                            }}
                            className="flex items-center justify-center gap-2 p-4 rounded-3xl border-2 border-dashed hover:bg-white/5 transition-all group"
                        >
                            <PlusIcon className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                            <span className="font-bold text-sm uppercase tracking-wider" style={stickerTextStyle(0.18)}>ADD PROFILE</span>
                        </button>
                    </div>
                </div>

                {/* Footer Tip */}
                <div className="p-4 bg-white/5 text-center">
                    <p
                        className="text-[9px] text-gray-500 uppercase tracking-widest font-black font-sans"
                        style={stickerTextStyle(0.18)}
                    >
                        Tip: Hold your profile button in the navbar to open
                    </p>
                </div>
            </div>
        </div>
    );
};
